import { Controller } from '@hotwired/stimulus';
import { confirmMessage } from './message_controller';

export default class extends Controller {
  static targets = ['checkbox', 'summary', 'back', 'cancel', 'submit'];
  static values = {
    noChangesText: String,
    oneChangeText: String,
    manyChangesText: String,
  };

  connect() {
    this.update();
  }

  update() {
    const changed = this.checkboxTargets.filter((checkbox) => {
      return this.initiallyChecked(checkbox) !== checkbox.checked;
    });

    const hasChanges = changed.length > 0;

    if (this.hasBackTarget) {
      this.backTarget.classList.toggle('hidden', hasChanges);
    }

    if (this.hasCancelTarget) {
      this.cancelTarget.classList.toggle('hidden', !hasChanges);
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.classList.toggle('hidden', !hasChanges);
    }

    if (changed.length === 0) {
      this.summaryTarget.textContent = this.noChangesTextValue || 'No unsaved changes';
      return;
    }

    const chips = changed
      .map((checkbox) => {
        const privilegeName = this.escapeHtml(checkbox.dataset.privilegeName || checkbox.value);
        const added = checkbox.checked;

        const chipClasses = added
          ? 'bg-confirmationLight text-primary'
          : 'bg-errorLight text-errorDark';

        const prefix = added ? '+' : '−';

        return `
          <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${chipClasses}">
            ${prefix} ${privilegeName}
          </span>
        `;
      })
      .join('');

    this.summaryTarget.innerHTML = `
        <div class="space-y-2">
          <div class="text-sm text-grey-3">
            ${this.unsavedChangesText(changed.length)}
          </div>

          <div class="flex flex-wrap gap-1">
            ${chips}
          </div>
        </div>
      `;
  }

  async applyTemplate(event) {
    const { templateName, privileges } = event.params;
    const privilegeNames = Array.isArray(privileges) ? privileges : [];

    const confirmed = await confirmMessage(
      `Apply "${this.humanize(templateName)}" template? This will replace the currently selected privileges on this page.`
    );

    if (!confirmed) return;

    const privilegeSet = new Set(privilegeNames);

    this.checkboxTargets.forEach((checkbox) => {
      const key = checkbox.dataset.privilegeKey;
      checkbox.checked = privilegeSet.has(key);
    });

    this.update();
  }

  reset() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = this.initiallyChecked(checkbox);
    });

    this.update();
  }

  initiallyChecked(checkbox) {
    return checkbox.dataset.initiallyChecked === 'true';
  }

  humanize(value) {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  unsavedChangesText(count) {
    if (count === 1 && this.hasOneChangeTextValue) {
      return this.oneChangeTextValue;
    }

    if (this.hasManyChangesTextValue) {
      return this.manyChangesTextValue.replace('%{count}', count);
    }

    return `${count} unsaved changes`;
  }
}
